import React, { useEffect, useRef } from 'react';
import { useDrafts } from '../contexts/DraftContext';
import { useKeyboardOffset } from '../utils/useKeyboardOffset';
import LexicalEditor from './LexicalEditor';
import { LinkedPostBar } from './LinkedPostBar';

interface EditorProps {
  isEditable?: boolean;
}

const Editor: React.FC<EditorProps> = ({ isEditable = true }) => {
  const { currentDraft, saveDraft, linkedPost, linkedPostMode, clearLinkedPost, toggleLinkedPostMode } = useDrafts();
  const kbOffset = useKeyboardOffset();
  const editorRef = useRef<HTMLDivElement>(null);

  const placeholderText = "what's happening?"

  // When the keyboard opens, wait for the viewport to stabilize then scroll the editor visible.
  // The spacer div below (kbOffset height) ensures there's enough scrollable room to clear the keyboard.
  useEffect(() => {
    if (kbOffset <= 0) return;
    let rafId: number;
    let lastHeight = window.visualViewport?.height ?? window.innerHeight;
    let stableFrames = 0;

    let iterations = 0;
    const checkStable = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      if (Math.abs(h - lastHeight) < 1) {
        stableFrames++;
      } else {
        stableFrames = 0;
        lastHeight = h;
      }
      if (stableFrames >= 3 || ++iterations > 60) {
        editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }
      rafId = requestAnimationFrame(checkStable);
    };
    rafId = requestAnimationFrame(checkStable);
    return () => cancelAnimationFrame(rafId);
  }, [kbOffset]);

  // Robust: Only render LexicalEditor when currentDraft is loaded
  return (
    <div className="editor-layout" ref={editorRef}>
      {/* <div className="editor-top">
        <button 
            className="user-avatar"
            onClick={handleAvatarClick}
            aria-label="Open account settings"
          >
            {profilePic ? (
              <img 
                src={profilePic} 
                alt="Profile" 
                className="avatar-image"
              />
            ) : (
              <div className="avatar-placeholder" />
            )}
          </button>
          <div className="user-label">You v0.1</div> 
      </div> */}
      {(!currentDraft || typeof currentDraft.raw !== 'string' || currentDraft.raw.trim() === '') ? (
        <div style={{ color: '#888', padding: '2rem', textAlign: 'center' }}>Loading draft...</div>
      ) : (
        <div key={currentDraft.id}>
          {linkedPost && linkedPostMode && (
            <LinkedPostBar
              post={linkedPost}
              mode={linkedPostMode}
              onToggleMode={toggleLinkedPostMode}
              onClear={clearLinkedPost}
            />
          )}
          <LexicalEditor
            key={currentDraft.id}
            initialValue={currentDraft.raw}
            onChange={(editorStateString) => {
              if (currentDraft) {
                saveDraft(editorStateString, currentDraft.platforms);
              }
            }}
            placeholder={placeholderText}
            isEditable={isEditable}
          />
        </div>
      )}
      {kbOffset > 0 && <div style={{ height: kbOffset }} />}
    </div>
  );
};

export default Editor; 
